import { useState, useEffect, useCallback } from 'react';
import { productsAPI, storesAPI, categoriesAPI, mediaAPI, uploadToCloudinary, aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ProductVariationsEditor } from '../components/ProductVariationsEditor';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Upload,
  Package,
  Image,
  X,
  Loader2,
  Star,
  Sparkles,
  Film,
  ListChecks,
  Info,
} from 'lucide-react';

const INITIAL_FORM = {
  store_id: '',
  category_id: '',
  name: '',
  description: '',
  price: '',
  promo_price: '',
  images: [],
  in_stock: true,
  featured: false,
  variations: [],
  // Etapa 2 — Página Completa do Produto
  video_url: '',
  specs: [], // [{key, value}]
  additional_info: '',
};

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, [storeFilter]);

  const loadData = async () => {
    try {
      const [productsRes, storesRes, categoriesRes] = await Promise.all([
        productsAPI.list({ store_id: storeFilter || undefined }),
        storesAPI.list({}),
        categoriesAPI.list()
      ]);
      setProducts(productsRes.data);
      setStores(storesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const newImages = [...formData.images];

    for (const file of files) {
      try {
        const result = await uploadToCloudinary(file, 'image', 'products');
        if (result.secure_url) {
          newImages.push(result.secure_url);
        }
      } catch (error) {
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }

    setFormData({ ...formData, images: newImages });
    setUploading(false);
    toast.success('Imagens enviadas!');
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadToCloudinary(file, 'video', 'products');
      if (result.secure_url) {
        setFormData((s) => ({ ...s, video_url: result.secure_url }));
        toast.success('Vídeo enviado!');
      }
    } catch (error) {
      toast.error(`Erro ao enviar vídeo`);
    } finally {
      setUploading(false);
    }
  };

  const addSpec = () => {
    setFormData((s) => ({ ...s, specs: [...(s.specs || []), { key: '', value: '' }] }));
  };
  const updateSpec = (index, field, value) => {
    setFormData((s) => ({
      ...s,
      specs: (s.specs || []).map((sp, i) => (i === index ? { ...sp, [field]: value } : sp)),
    }));
  };
  const removeSpec = (index) => {
    setFormData((s) => ({ ...s, specs: (s.specs || []).filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.store_id) {
      toast.error('Selecione uma loja');
      return;
    }
    
    setSubmitting(true);

    try {
      // Validate variations before submit — warn if any group has no name or no options
      const invalidGroups = (formData.variations || []).filter((g) => {
        const hasName = (g.name || '').trim();
        const hasValidOption = (g.options || []).some((o) => (o.label || '').trim());
        return !hasName || !hasValidOption;
      });
      if (invalidGroups.length > 0) {
        toast.error('Preencha o nome do grupo e pelo menos uma opção com rótulo em cada variação.');
        setSubmitting(false);
        return;
      }

      // Normalize variations: strip empty groups/options, ensure price_delta is a number
      const cleanVariations = (formData.variations || [])
        .map((g) => ({
          id: g.id,
          name: (g.name || '').trim(),
          options: (g.options || [])
            .filter((o) => (o.label || '').trim())
            .map((o) => ({
              id: o.id,
              label: (o.label || '').trim(),
              price_delta: Number(o.price_delta) || 0,
              image_url: o.image_url || '',
            })),
        }))
        .filter((g) => g.name && g.options.length > 0);

      const data = {
        ...formData,
        price: parseFloat(formData.price),
        promo_price: formData.promo_price ? parseFloat(formData.promo_price) : null,
        category_id: formData.category_id || null,
        variations: cleanVariations,
        // Sanitize Etapa 2 fields — drop empty spec rows
        specs: (formData.specs || [])
          .map((sp) => ({ key: (sp.key || '').trim(), value: (sp.value || '').trim() }))
          .filter((sp) => sp.key && sp.value),
        video_url: (formData.video_url || '').trim(),
        additional_info: (formData.additional_info || '').trim(),
      };

      if (editingProduct) {
        await productsAPI.update(editingProduct.id, data);
        toast.success('Produto atualizado!');
      } else {
        await productsAPI.create(data);
        toast.success('Produto cadastrado!');
      }
      
      setDialogOpen(false);
      setFormData(INITIAL_FORM);
      setEditingProduct(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      store_id: product.store_id,
      category_id: product.category_id || '',
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      promo_price: product.promo_price?.toString() || '',
      images: product.images || [],
      in_stock: product.in_stock,
      featured: product.featured,
      variations: product.variations || [],
      video_url: product.video_url || '',
      specs: product.specs || [],
      additional_info: product.additional_info || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await productsAPI.delete(productToDelete.id);
      toast.success('Produto excluído!');
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.store_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="products-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Produtos</h1>
          <p className="text-slate-500 mt-1">Gerencie os produtos das lojas</p>
        </div>
        <Button
          onClick={() => { setEditingProduct(null); setFormData(INITIAL_FORM); setDialogOpen(true); }}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar produto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={storeFilter || "all"} onValueChange={(v) => setStoreFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-64" data-testid="store-filter">
                <SelectValue placeholder="Todas as lojas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as lojas</SelectItem>
                {stores.map((store) => (
                  <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-slate-400">
          <Package className="w-12 h-12 mb-4" />
          <p className="text-lg font-medium">Nenhum produto encontrado</p>
          <p className="text-sm">Cadastre seu primeiro produto</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden" data-testid={`product-card-${product.id}`}>
              <div className="relative aspect-square bg-slate-100">
                {product.images?.[0] ? (
                  <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-12 h-12 text-slate-300" />
                  </div>
                )}
                {product.featured && (
                  <span className="absolute top-2 right-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded flex items-center gap-1">
                    <Star className="w-3 h-3" /> Destaque
                  </span>
                )}
                {product.promo_price && (
                  <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                    OFERTA
                  </span>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 truncate">{product.name}</h3>
                <p className="text-sm text-slate-500 truncate">{product.store_name}</p>
                <div className="mt-2 flex items-center gap-2">
                  {product.promo_price ? (
                    <>
                      <span className="text-slate-400 line-through text-sm">{formatPrice(product.price)}</span>
                      <span className="font-bold text-green-600">{formatPrice(product.promo_price)}</span>
                    </>
                  ) : (
                    <span className="font-bold text-slate-900">{formatPrice(product.price)}</span>
                  )}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(product)} className="flex-1">
                    <Pencil className="w-4 h-4 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => { setProductToDelete(product); setDeleteDialogOpen(true); }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Loja *</Label>
                <Select
                  value={formData.store_id}
                  onValueChange={(value) => setFormData({ ...formData, store_id: value })}
                >
                  <SelectTrigger data-testid="select-store">
                    <SelectValue placeholder="Selecione a loja" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((store) => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select
                  value={formData.category_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, category_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="select-category">
                    <SelectValue placeholder="Selecione (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem categoria</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.icon} {cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Nome do Produto *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Camiseta Básica Azul"
                required
                data-testid="input-name"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Descrição</Label>
                <AIDescriptionButton
                  productName={formData.name}
                  categoryId={formData.category_id}
                  onGenerated={(text) => setFormData((s) => ({ ...s, description: text }))}
                />
              </div>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva o produto... ou clique em 'Gerar com IA'"
                rows={4}
                data-testid="input-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Preço *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="0,00"
                  required
                  data-testid="input-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Preço Promocional</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.promo_price}
                  onChange={(e) => setFormData({ ...formData, promo_price: e.target.value })}
                  placeholder="0,00 (opcional)"
                  data-testid="input-promo-price"
                />
              </div>
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Fotos do Produto</Label>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleImageUpload(Array.from(e.target.files))}
                  className="hidden"
                  id="image-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="image-upload"
                  className="flex flex-col items-center justify-center cursor-pointer py-4"
                >
                  {uploading ? (
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mb-2" />
                      <span className="text-sm text-slate-500">Clique para adicionar fotos</span>
                    </>
                  )}
                </label>
                
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden group">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Variações do Produto (EXCLUSIVO ADMIN) */}
            <ProductVariationsEditor
              value={formData.variations}
              onChange={(v) => setFormData({ ...formData, variations: v })}
            />

            {/* Etapa 2 — Vídeo do Produto */}
            <div className="space-y-2 border-t pt-4">
              <Label className="flex items-center gap-2 text-slate-800 font-semibold">
                <Film className="w-4 h-4 text-blue-600" /> Vídeo do Produto (opcional)
              </Label>
              <p className="text-xs text-slate-500">Cole uma URL (MP4/Cloudinary/YouTube MP4) ou envie um arquivo de vídeo.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://... .mp4"
                  className="flex-1"
                  data-testid="input-video-url"
                />
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleVideoUpload(e.target.files?.[0])}
                  className="hidden"
                  id="video-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="video-upload"
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                  data-testid="upload-video-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Enviar vídeo
                </label>
                {formData.video_url && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFormData({ ...formData, video_url: '' })}
                    data-testid="remove-video-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {formData.video_url && (
                <video src={formData.video_url} controls className="w-full max-h-52 rounded-md bg-black" data-testid="video-preview">
                  <track kind="captions" />
                </video>
              )}
            </div>

            {/* Etapa 2 — Características Técnicas */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-slate-800 font-semibold">
                  <ListChecks className="w-4 h-4 text-blue-600" /> Características Técnicas (opcional)
                </Label>
                <Button type="button" size="sm" variant="outline" onClick={addSpec} data-testid="add-spec-btn">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar
                </Button>
              </div>
              <p className="text-xs text-slate-500">Lista tipo tabela (ex.: Marca / Nike, Material / Mesh 3D). Aparece na página do produto.</p>
              {(formData.specs || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic">Nenhuma característica adicionada.</p>
              ) : (
                <div className="space-y-2">
                  {formData.specs.map((sp, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`spec-row-${i}`}>
                      <Input
                        value={sp.key}
                        onChange={(e) => updateSpec(i, 'key', e.target.value)}
                        placeholder="Característica (ex.: Marca)"
                        className="flex-1"
                        data-testid={`spec-key-${i}`}
                      />
                      <Input
                        value={sp.value}
                        onChange={(e) => updateSpec(i, 'value', e.target.value)}
                        placeholder="Valor (ex.: Nike)"
                        className="flex-1"
                        data-testid={`spec-value-${i}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="text-red-600"
                        onClick={() => removeSpec(i)}
                        data-testid={`spec-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Etapa 2 — Informações Importantes */}
            <div className="space-y-2 border-t pt-4">
              <Label className="flex items-center gap-2 text-slate-800 font-semibold">
                <Info className="w-4 h-4 text-blue-600" /> Informações Importantes (opcional)
              </Label>
              <p className="text-xs text-slate-500">Dicas de frete, prazos de troca, cuidados, garantia — aparece em destaque na página do produto.</p>
              <Textarea
                value={formData.additional_info}
                onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                placeholder="• Frete grátis acima de R$ 300...&#10;• Prazo de troca: 30 dias..."
                rows={4}
                data-testid="input-additional-info"
              />
            </div>

            {/* Switches */}
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.in_stock}
                  onCheckedChange={(checked) => setFormData({ ...formData, in_stock: checked })}
                  data-testid="switch-in-stock"
                />
                <Label>Em estoque</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                  data-testid="switch-featured"
                />
                <Label>Produto em destaque</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={submitting} data-testid="submit-product-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingProduct ? 'Salvar' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Tem certeza que deseja excluir o produto <strong>{productToDelete?.name}</strong>?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;

// AI Description generator button
const AIDescriptionButton = ({ productName, categoryId, onGenerated }) => {
  const [loading, setLoading] = useState(false);
  const handleClick = async () => {
    if (!productName?.trim()) {
      toast.error('Informe o nome do produto primeiro');
      return;
    }
    setLoading(true);
    try {
      const { data } = await aiAPI.generateDescription({
        product_name: productName,
        category: categoryId || '',
        tone: 'vendedor',
      });
      onGenerated(data.description);
      toast.success('Descrição gerada pela IA!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao gerar descrição');
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-200 text-blue-700 hover:from-blue-100 hover:to-violet-100 disabled:opacity-50 transition-all"
      data-testid="ai-generate-description-btn"
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      Gerar com IA
    </button>
  );
};
